/**
 * Entries of the hamburger menu: links, and labels opening a sub-menu of links
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { NavigationItem } from './top-menu.logic';

const CLOSE_DELAY_MS = 400;

export interface NavigationMenuProps {
  items: readonly NavigationItem[];
  onLogout: () => void;
}

export function NavigationMenu({
  items,
  onLogout,
}: Readonly<NavigationMenuProps>) {
  const [openChildren, setOpenChildren] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(
      () => setOpenChildren(null),
      CLOSE_DELAY_MS,
    );
  };
  useEffect(() => cancelClose, []);

  return (
    <div
      role="menu"
      aria-label="Navigation"
      className="absolute left-0 top-12 z-20 min-w-[200px] bg-white py-2 shadow-lg"
      data-testid="header-nav-menu"
    >
      {items.map((item) =>
        item.children ? (
          <div
            key={item.label}
            className="relative"
            onMouseEnter={() => {
              cancelClose();
              setOpenChildren(item.label);
            }}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={openChildren === item.label}
              className="nav-menu-item justify-between"
              onClick={() =>
                setOpenChildren((open) =>
                  open === item.label ? null : item.label,
                )
              }
            >
              <span>{item.label}</span>
              <span className="submenu-arrow" aria-hidden="true" />
            </button>
            {openChildren === item.label && (
              <div
                role="menu"
                aria-label={item.label}
                className="child-menu-panel absolute left-full top-0 z-30 min-w-[180px] bg-white py-2 shadow-lg"
              >
                {item.children.map((child) => (
                  <Link
                    key={child.link}
                    to={child.link}
                    role="menuitem"
                    className="nav-menu-item"
                    activeProps={{ className: 'nav-menu-item active' }}
                    onClick={() => setOpenChildren(null)}
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Link
            key={item.link}
            to={item.link}
            role="menuitem"
            className="nav-menu-item"
            activeProps={{ className: 'nav-menu-item active' }}
          >
            {item.label}
          </Link>
        ),
      )}
      <button
        type="button"
        role="menuitem"
        onClick={onLogout}
        className="nav-menu-item"
        data-testid="header-logout-button"
      >
        Logout
      </button>
    </div>
  );
}
